import { useState, type ReactNode } from "react";

import { makePostApiCall } from "../../../shared/makeApiCall";
import { useToast } from "../../../contexts/ToastContext";

import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import type { EmptyModalState } from "../../../components/modals/EmptyModal";
import type { BreadcrumbPage } from "../../../components/controls/BreadCrumbs";
import type {
  CategoryFormValues,
  CategoryMutationResponse,
  CategoryPayload,
  ProductCategory,
} from "../../../types/categories";

export function ProductCategoryDetailsViewModel(logout: () => void, categoryId?: string) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState<ProductCategory | null>(null);

  const [breadCrumbs, setBreadCrumbs] = useState<BreadcrumbPage[]>([
    {
      name: "Primary Categories",
      href: "/primary-categories",
      current: false,
    },
    { name: "Create New", href: "#", current: true },
  ]);

  const showErrorModal = (title: string, subtitle = "Please try again.") => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getCategory = async function (search: string | null = null, page = 1) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    const callResult = await makePostApiCall<ProductCategory, typeof payload>(
      `products/categories/${categoryId}`,
      payload
    );
    if (callResult.success) {
      setCategory(callResult.data);
      setBreadCrumbs([
        {
          name: "Primary Categories",
          href: "/primary-categories",
          current: false,
        },
        { name: callResult.data.name, href: "#", current: true },
      ]);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch category");
    }
    setLoading(false);
  };

  const createCategory = async function (values: CategoryFormValues) {
    const payload: CategoryPayload = { ...values, is_default: values.is_default ? 1 : 0 };
    const callResult = await makePostApiCall<CategoryMutationResponse, CategoryPayload>(
      `products/categories/create`,
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(callResult.data.message || "Action completed");
      window.location.assign("/primary-categories/" + callResult.data.id);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not create category");
    }
  };

  const updateCategory = async function (values: CategoryFormValues) {
    const payload: CategoryPayload = { ...values, is_default: values.is_default ? 1 : 0 };
    const callResult = await makePostApiCall<CategoryMutationResponse, CategoryPayload>(
      `updatePrimaryCategory/${categoryId}`,
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(callResult.data.message || "Action completed");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not update category");
    }
  };

  return {
    createCategory,
    updateCategory,
    getCategory,
    category,
    loading,
    emptyModal,
    breadCrumbs,
  };
}
